/**
 * The one implementation of the agent stream's SSE framing, for every client.
 *
 * `/api/agent/stream` and `/api/agent/conversation/stream` both answer with
 * newline-delimited `data: {"delta"|"error": ...}` frames. That format was
 * being decoded in THREE hand-synced places: the web tutor overlay
 * (`useAgentStream`), the web scenario runner (`scenarioStream`), and the
 * native `sseClient`. They had already drifted, and the drift was a bug:
 *
 *   A delivery from `reader.read()` is a slice of BYTES, not a slice of
 *   frames. The network is free to end one mid-frame. Both web copies split
 *   each delivery on "\n" and threw the remainder away, so a frame cut in half
 *   parsed as malformed (the head) and then as a non-`data: ` line (the tail),
 *   and BOTH halves were skipped by the "malformed line - skip" catch. The
 *   learner silently lost a span of the tutor's answer, with nothing logged.
 *   The native client had already fixed this with a residual buffer; the fix
 *   was never carried back.
 *
 * Holding the partial line is the entire subtlety here, which is exactly why
 * it belongs in one tested module rather than in three stream loops that each
 * have their own React state plumbing wrapped around them. Callers keep their
 * own transport, cancellation and rendering; they hand over decoded text and
 * get back frames.
 *
 * Zero imports, so this crosses the src/ios purity firewall untouched.
 */

/** The only line prefix the routes emit. Anything else is ignored. */
export const SSE_DATA_PREFIX = "data: ";

/** A decoded frame. Both fields are optional: the routes send one or the other. */
export type SseFrame = { delta?: string; error?: string };

export type SseFrameReader = {
  /**
   * Feed one decoded delivery. Returns the frames that are now COMPLETE;
   * a trailing partial line is retained for the next call.
   */
  push(chunk: string): SseFrame[];
  /**
   * Feed the end of the stream. Returns whatever the retained residual turned
   * out to be, which matters because a well-behaved server may end its last
   * frame without a trailing newline.
   */
  flush(): SseFrame[];
};

function parseLines(lines: string[]): SseFrame[] {
  const frames: SseFrame[] = [];
  for (const line of lines) {
    if (!line.startsWith(SSE_DATA_PREFIX)) continue;
    try {
      frames.push(JSON.parse(line.slice(SSE_DATA_PREFIX.length)) as SseFrame);
    } catch {
      // A malformed frame is skipped rather than thrown: one bad line must not
      // abort a stream that is otherwise delivering an answer.
    }
  }
  return frames;
}

export function createSseFrameReader(): SseFrameReader {
  let residual = "";
  return {
    push(chunk: string): SseFrame[] {
      residual += chunk;
      const lines = residual.split("\n");
      // The last element is either a partial line or the empty string left by a
      // trailing newline. Either way it is held, never parsed, until more text
      // arrives or the stream ends.
      residual = lines.pop() ?? "";
      return parseLines(lines);
    },
    flush(): SseFrame[] {
      const rest = residual;
      residual = "";
      return parseLines([rest]);
    },
  };
}
